import { Controller } from "@hotwired/stimulus";
import debounce from "debounce";

export default class extends Controller {
  static targets = ["mobileOverlay", "mobileInput", "mobileResults", "mobileSpinner"];
  static values = {
    url: String,
    minChars: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 300 }
  };

  connect() {
    this.hideResults();
    this.currentIndex = -1;
    this.debouncedSearch = debounce(this.performSearch.bind(this), this.debounceMs || 300);

    // Handle escape key for closing overlay
    this.handleEscapeBound = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.handleEscapeBound);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscapeBound);
    document.body.classList.remove("overflow-hidden");
  }

  handleEscape(event) {
    if (event.key === "Escape" && !this.mobileOverlayTarget.classList.contains("hidden")) {
      this.toggleMobileSearch(event);
    }
  }

  toggleMobileSearch(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.mobileOverlayTarget.classList.toggle("hidden");
    document.body.classList.toggle("overflow-hidden");

    if (!this.mobileOverlayTarget.classList.contains("hidden")) {
      this.mobileInputTarget.focus();
    } else {
      this.mobileInputTarget.value = "";
      this.hideResults();
    }
  }

  submitSearch(event) {
    event.preventDefault();
    const query = this.mobileInputTarget.value.trim();
    if (query) {
      const form = event.target.closest("form");
      if (form) {
        form.submit();
      }
    }
  }

  search() {
    const query = this.mobileInputTarget.value.trim();

    if (query.length < (this.minCharsValue || 2)) {
      this.hideResults();
      return;
    }

    this.showSpinner();
    this.debouncedSearch();
  }

  async performSearch() {
    const query = this.mobileInputTarget.value.trim();

    try {
      const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`);
      const html = await response.text();

      this.mobileResultsTarget.innerHTML = html;
      this.showResults();
    } catch (error) {
      console.error("Mobile search failed:", error);
    } finally {
      this.hideSpinner();
    }
  }

  keydown(event) {
    const results = this.mobileResultsTarget.querySelectorAll("[data-search-target='result']");

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.currentIndex = Math.min(this.currentIndex + 1, results.length - 1);
        this.highlightResult(results);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.currentIndex = Math.max(this.currentIndex - 1, -1);
        this.highlightResult(results);
        break;
      case "Enter":
        event.preventDefault();
        const query = this.mobileInputTarget.value.trim();
        if (this.currentIndex >= 0) {
          const selectedResult = results[this.currentIndex];
          if (selectedResult) {
            selectedResult.click();
          }
        } else if (query) {
          event.target.closest("form").submit();
        }
        break;
      case "Escape":
        event.preventDefault();
        this.toggleMobileSearch(event);
        break;
    }
  }

  highlightResult(results) {
    results.forEach((result, index) => {
      if (index === this.currentIndex) {
        result.classList.add("bg-orange-50");
      } else {
        result.classList.remove("bg-orange-50");
      }
    });
  }

  showResults() {
    this.mobileResultsTarget.classList.remove("hidden");
  }

  hideResults() {
    this.mobileResultsTarget.classList.add("hidden");
    this.currentIndex = -1;
  }

  showSpinner() {
    this.mobileSpinnerTarget.querySelector("div").classList.remove("hidden");
  }

  hideSpinner() {
    this.mobileSpinnerTarget.querySelector("div").classList.add("hidden");
  }
}
